<template>
	<header>
		<nav :class="[{ compact: isCompact }, { active: menuIsActive }, `scroll-${scrollDirection}`]">
			<div class="row nav-bar">
				<div class="columns column12 flex-row">
					<logo-button class="nav-bar-logo" :color="color" :hidden="logoIsHidden" @click="handleLogoClick" />
					<menu-button :color="color" :show-close-icon="menuIsActive" @toggle-menu="toggleMenu" />
					<div class="menu-items">
						<nuxt-link v-for="item in contentblocks" :key="item.ID" :to="'#' + item.header.trim()">
							{{ item.header }}
						</nuxt-link>
					</div>
					<div class="nav-buttons">
						<lang-select
							:fullpage-nav-active="menuIsActive"
							:color="color"
							:compact="isCompact"
							:items="page.langmenu"
						/>
						<book-button class="reserve-now" :color="color" :compact="isCompact">
							{{ defaults[locale].website.book }}
						</book-button>
					</div>
				</div>
			</div>
			<fullpage-nav
				:class="{ active: menuIsActive }"
				:menu="contentblocks"
				:socials="socials"
				@toggle-menu="toggleMenu"
				@handle-logo-click="handleLogoClick"
			/>
		</nav>
	</header>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localeRoute = useLocaleRoute();
const route = useRoute();
const scrollDirection = useScrollDirection();

defineProps({
	contentblocks: { type: Array, default: () => [] },
});

await fetchPage();
await fetchDefaults();

const menuIsActive = ref(false);

const toggleMenu = () => {
	menuIsActive.value = !menuIsActive.value;
	document.body.classList.toggle('locked');
};

const handleLogoClick = () => {
	if (menuIsActive.value) {
		toggleMenu();
	}

	if (route.name?.startsWith('index')) {
		window.scrollTo(0, 0);
	} else {
		const route = localeRoute('index');
		navigateTo(route.fullPath);
	}
};

const color = computed(() => {
	if (menuIsActive.value) {
		return 'medium';
	} else if (scrollDirection.value === 'up' || !page.value.headingImage) {
		return 'dark';
	} else {
		return 'light';
	}
});

const isCompact = computed(() => {
	if (scrollDirection.value === 'up' && !menuIsActive.value) {
		return true;
	} else if (scrollDirection.value === 'down' && window.scrollY > 500 && !menuIsActive.value) {
		return true;
	} else {
		return false;
	}
});

const logoIsHidden = computed(() => {
	return route.name.startsWith('index') && !menuIsActive.value && !(scrollDirection.value === 'up');
});
</script>

<style lang="scss" scoped>
nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	padding: 60px 0 0;
	overflow: visible;
	background: transparent;
	transition:
		top 0.3s ease-in-out,
		padding 0.3s ease-in-out,
		height 0.3s ease-in-out,
		background 0.3s ease-in-out;

	.reserve-now {
		text-transform: uppercase;
		font-weight: 700;
		letter-spacing: 3px;
		font-family: var(--button-font-family);
		font-size: 15px;
		line-height: 28px;

		&::after {
			opacity: 0;
		}

		&.compact {
			&::after {
				content: '';
				display: block;
				width: 0;
				height: 2px;
				background: var(--button-nofill-hover);
				position: absolute;
				bottom: -2px;
				left: 0;
				right: 0;
				transition: width 0.3s ease-in-out;
				opacity: 1;
			}
		}

		&:hover {
			&::after {
				width: 100%;
			}
		}
	}

	.menu-items {
		position: absolute;
		top: 19px;
		left: 0;
		right: 0;
		z-index: 1;
		text-align: center;

		a {
			font-family: var(--button-font-family);
			color: #fff;
			font-size: 15px;
			text-transform: uppercase;
			margin: 0 15px;
			font-weight: 700;
			letter-spacing: 2px;
		}
	}

	&.compact {
		padding: 14px 0;
	}

	&.scroll-down {
		top: -150px;
	}

	&.scroll-up {
		background: #fff;
		top: 0;

		.menu-items {
			top: 11px;

			a {
				color: #000;

				&:hover {
					color: var(--cta-color);
				}
			}
		}

		.flex-row {
			align-items: flex-start;
		}

		.nav-buttons {
			align-items: flex-start;
		}

		.logo.nav-bar-logo {
			top: 5px;
		}

		.reserve-now.compact {
			margin: 4px 0 0;
		}
	}

	&.active {
		top: 0;

		.flex-row {
			align-items: center;
		}

		.nav-buttons {
			align-items: center;
		}

		.logo.nav-bar-logo {
			top: auto;
		}
	}

	.row {
		overflow: visible;
		z-index: 102;
		position: relative;
	}
}

nav .flex-row,
.nav-buttons {
	justify-content: space-between;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
}

.nav-buttons {
	gap: 22px;
	animation: fade-from-right 0.7s ease;
}

@media (min-width: 921px) {
	.nav-buttons {
		position: relative;
		z-index: 2;
	}
}

@media (max-width: 920px) {
	nav {
		padding: 30px 0 0;

		.reserve-now {
			font-size: 13px;
		}

		.menu-items {
			display: none;
		}

		.row {
			padding-left: 30px;
			padding-right: 30px;
		}
	}
}

@media (max-width: 600px) {
	nav {
		.flex-row {
			padding: 0;
		}
	}
}
</style>
